<script>
    import { selectedItem } from '../../store/store.js';
    import { displayInt } from '../../utils/utils.js';
    import { onMount, onDestroy } from 'svelte';
    import { Chart, registerables } from 'chart.js';

    // Props
    /** @type {string} The title displayed above the chart. */
    export let headerTitle = "Historik";
    /** @type {Array<Object>} Configuration for each dataset.
     * Example: [{ label, dataKey, borderColor, pointBackgroundColor, fill, gradientColors?: {start, stop}, yAxisID, order?, tooltipSuffix? }]
     */
    export let datasetsConfig = [];
    /** @type {Array<Object>} Configuration for Y-axes.
     * Example: [{ id, title, position, grid?: {drawOnChartArea}, beginAtZero? }]
     */
    export let yAxesConfig = [];
    /** @type {string} The title for the X-axis. */
    export let xAxisTitleText = "År";

    // Chart.js defaults
    Chart.defaults.font.family = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
    Chart.defaults.color = '#FFFFFF';
    Chart.defaults.font.size = 12;
    Chart.register(...registerables);

    let historyData = {};
    let years = [];
    let chartCanvas; // Reference to the canvas element
    let historyChart = null; // Variable to hold the chart instance

    $: if ($selectedItem && $selectedItem.core_history && $selectedItem.core_history.history_by_year) {
        historyData = $selectedItem.core_history.history_by_year;
        years = Object.keys(historyData).map(Number).sort((a, b) => a - b);

        if (historyChart) {
            updateChartData();
        } else {
            // Ensure chart is created if canvas is ready, data and configs are available
            if (chartCanvas && years.length > 0 && datasetsConfig.length > 0 && yAxesConfig.length > 0) {
                 setTimeout(createChart, 0);
            }
        }
    }

    function createChart() {
        if (!chartCanvas || !years.length || !$selectedItem || !datasetsConfig.length || !yAxesConfig.length) return;
        const ctx = chartCanvas.getContext('2d');

        const chartDatasets = datasetsConfig.map(dsConfig => {
            let backgroundColor = dsConfig.borderColor; // Default if not filled or no gradient
            if (dsConfig.fill && dsConfig.gradientColors) {
                const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                gradient.addColorStop(0, dsConfig.gradientColors.start);
                gradient.addColorStop(1, dsConfig.gradientColors.stop);
                backgroundColor = gradient;
            } else if (dsConfig.fill && !dsConfig.gradientColors) {
                // If fill is true but no gradientColors, use a semi-transparent version of borderColor or a default
                // For now, it will use borderColor which might not be what's intended for a fill.
                // It's recommended to provide gradientColors if fill is true and a gradient is desired.
                // Or, one could define a default fill behavior here.
            }


            return {
                label: dsConfig.label,
                data: years.map(year => historyData[year]?.[dsConfig.dataKey] / 1000 ?? null),
                fill: dsConfig.fill,
                borderColor: dsConfig.borderColor,
                backgroundColor: backgroundColor,
                tension: dsConfig.tension ?? 0.4,
                pointRadius: dsConfig.pointRadius ?? 3,
                pointBackgroundColor: dsConfig.pointBackgroundColor,
                pointHoverRadius: dsConfig.pointHoverRadius ?? 6,
                yAxisID: dsConfig.yAxisID,
                order: dsConfig.order
            };
        });

        const scalesOptions = {
            x: {
                title: {
                    display: true,
                    text: xAxisTitleText,
                    font: { size: 14 },
                    color: '#FFFFFF'
                },
                grid: {
                    display: false
                },
                ticks: {
                    color: '#FFFFFF'
                }
            }
        };

        yAxesConfig.forEach(axisConfig => {
            scalesOptions[axisConfig.id] = {
                type: 'linear',
                display: true,
                position: axisConfig.position,
                title: {
                    display: true,
                    text: axisConfig.title,
                    font: { size: 14 },
                    color: '#FFFFFF'
                },
                beginAtZero: axisConfig.beginAtZero ?? true,
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)', // Default grid color
                    ...(axisConfig.grid || {}) // Allow overriding, e.g., drawOnChartArea: false or different color
                },
                ticks: {
                    color: '#FFFFFF',
                    callback: function(value) {
                        return displayInt(value);
                    },
                    ...(axisConfig.ticks || {})
                }
            };
        });

        const chartData = {
            labels: years,
            datasets: chartDatasets
        };

        const config = {
            type: 'line',
            data: chartData,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {
                            usePointStyle: true,
                            padding: 20,
                            color: '#FFFFFF'
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: '#FFFFFF',
                        bodyColor: '#FFFFFF',
                        callbacks: {
                            label: function(context) {
                                let label = context.dataset.label || '';
                                const currentDatasetConfig = datasetsConfig[context.datasetIndex];
                                const suffix = currentDatasetConfig?.tooltipSuffix ?? '';

                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    label += displayInt(context.parsed.y) + suffix;
                                }
                                return label;
                            }
                        }
                    }
                },
                scales: scalesOptions,
                interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                }
            }
        };

        if (historyChart) {
            historyChart.destroy();
        }
        historyChart = new Chart(chartCanvas, config);
    }

    function updateChartData() {
        if (!historyChart || !years.length || !datasetsConfig.length) return;

        historyChart.data.labels = years;
        datasetsConfig.forEach((dsConfig, index) => {
            if (historyChart.data.datasets[index]) {
                historyChart.data.datasets[index].data = years.map(year => historyData[year]?.[dsConfig.dataKey] / 1000 ?? null);
            }
        });
        historyChart.update();
    }

    onMount(() => {
        if (chartCanvas && years.length > 0 && datasetsConfig.length > 0 && yAxesConfig.length > 0) {
             setTimeout(createChart, 0);
        }
    });

    onDestroy(() => {
        if (historyChart) {
            historyChart.destroy();
            historyChart = null;
        }
    });
</script>

<div class="history-box box">
    <h4>{headerTitle}</h4>
    <div class="chart-container">
        <canvas bind:this={chartCanvas}></canvas>
    </div>
</div>

<style>
    .history-box {
        background-color: #18181a;
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        margin-bottom: 20px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        color: #FFFFFF;
    }

    .history-box h4 {
        font-size: 1.1rem;
        font-weight: 600;
        color: #FFFFFF;
        margin-bottom: 15px;
    }

    .chart-container {
        position: relative;
        height: 300px;
        width: 100%;
        margin-bottom: 20px;
    }
</style> 